import { useEffect, useState } from "react";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Nav } from "@/components/Nav";
import { OperatorForm } from "@/components/OperatorForm";
import { SectionLabel } from "@/components/Status";
import { LOCALE, useLang } from "@/i18n";
import { api, errCode } from "@/lib/api";

const Stat = ({ label, value, testId }) => (
  <div className="panel p-5" data-testid={testId}>
    <div className="mono-label mb-2">{label}</div>
    <div className="font-mono text-3xl text-vox-text">{value ?? "—"}</div>
  </div>
);

export default function Admin() {
  const { t, lang } = useLang();
  const [stats, setStats] = useState(null);
  const [orgs, setOrgs] = useState(null);
  const [users, setUsers] = useState(null);
  const [events, setEvents] = useState(null);

  const load = async () => {
    try {
      const [s, o, u, a] = await Promise.all([api.get("/admin/stats"), api.get("/admin/organizations"), api.get("/admin/users"), api.get("/admin/audit?limit=100")]);
      setStats(s.data);
      setOrgs(o.data);
      setUsers(u.data);
      setEvents(a.data);
    } catch (e) {
      toast.error(t(`errors.${errCode(e)}`, errCode(e)));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const toggleAi = async (org, value) => {
    setOrgs((prev) => prev.map((o) => (o.id === org.id ? { ...o, ai_enabled: value } : o)));
    try {
      await api.patch(`/admin/organizations/${org.id}`, { ai_enabled: value });
      setStats((s) => (s ? { ...s, ai_enabled_orgs: s.ai_enabled_orgs + (value ? 1 : -1) } : s));
    } catch (e) {
      toast.error(t(`errors.${errCode(e)}`, errCode(e)));
      load();
    }
  };

  return (
    <div className="min-h-screen" data-testid="platform-admin-page">
      <Nav />
      <main className="mx-auto max-w-6xl px-6 py-12">
        <div className="reveal mb-12">
          <div className="mono-label mb-3">{t("nav.admin")}</div>
          <h1 className="text-4xl font-semibold tracking-tight text-vox-text sm:text-5xl">{t("admin.title")}</h1>
        </div>
        <div className="reveal reveal-1 mb-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <Stat label={t("admin.orgs")} value={stats?.organizations} testId="stat-orgs" />
          <Stat label={t("admin.users")} value={stats?.users} testId="stat-users" />
          <Stat label={t("admin.meetings")} value={stats?.meetings} testId="stat-meetings" />
          <Stat label={t("admin.jobs")} value={stats?.jobs} testId="stat-jobs" />
          <Stat label={`${t("admin.orgs")} ${t("admin.aiOrgs")}`} value={stats?.ai_enabled_orgs} testId="stat-ai-orgs" />
        </div>

        <section className="reveal reveal-2 mb-12">
          <SectionLabel index="01">{t("admin.orgs")}</SectionLabel>
          {!orgs ? <div className="flex items-center gap-2 text-sm text-vox-dim"><Loader2 size={14} className="animate-spin" />{t("common.loading")}</div> : (
            <div className="overflow-x-auto rounded-2xl border border-vox-line">
              <table className="w-full text-sm" data-testid="platform-orgs-table">
                <thead className="bg-vox-deep/60 font-mono text-[11px] uppercase tracking-wider text-vox-dim">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium">{t("org.name")}</th>
                    <th className="px-4 py-3 text-left font-medium">{t("admin.owner")}</th>
                    <th className="px-4 py-3 text-left font-medium">{t("org.members")}</th>
                    <th className="px-4 py-3 text-left font-medium">{t("admin.meetings")}</th>
                    <th className="px-4 py-3 text-left font-medium">{t("admin.jobs")}</th>
                    <th className="px-4 py-3 text-left font-medium">{t("admin.created")}</th>
                    <th className="px-4 py-3 text-left font-medium">{t("admin.aiToggle")}</th>
                  </tr>
                </thead>
                <tbody>
                  {orgs.map((o) => (
                    <tr key={o.id} className="border-t border-vox-line" data-testid="platform-org-row">
                      <td className="px-4 py-3 text-vox-text" data-testid="platform-org-name">{o.name}</td>
                      <td className="px-4 py-3 text-vox-muted">{o.owner_email}</td>
                      <td className="px-4 py-3 font-mono text-vox-muted">{o.members_count}</td>
                      <td className="px-4 py-3 font-mono text-vox-muted">{o.meetings_count}</td>
                      <td className="px-4 py-3 font-mono text-vox-muted">{o.jobs_count}</td>
                      <td className="px-4 py-3 font-mono text-xs text-vox-dim">{new Date(o.created_at).toLocaleDateString(LOCALE[lang])}</td>
                      <td className="px-4 py-3"><Switch checked={o.ai_enabled} onCheckedChange={(v) => toggleAi(o, v)} data-testid="ai-enabled-toggle-switch" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="reveal reveal-3 mb-12">
          <SectionLabel index="02">{t("admin.users")}</SectionLabel>
          {!users ? null : (
            <div className="overflow-x-auto rounded-2xl border border-vox-line">
              <table className="w-full text-sm" data-testid="platform-users-table">
                <thead className="bg-vox-deep/60 font-mono text-[11px] uppercase tracking-wider text-vox-dim">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium">{t("auth.name")}</th>
                    <th className="px-4 py-3 text-left font-medium">{t("auth.email")}</th>
                    <th className="px-4 py-3 text-left font-medium">{t("org.title")}</th>
                    <th className="px-4 py-3 text-left font-medium">{t("org.role")}</th>
                    <th className="px-4 py-3 text-left font-medium">Auth</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-t border-vox-line" data-testid="platform-user-row">
                      <td className="px-4 py-3 text-vox-text">{u.name}</td>
                      <td className="px-4 py-3 text-vox-muted">{u.email}</td>
                      <td className="px-4 py-3 text-vox-muted">{u.org_name}</td>
                      <td className="px-4 py-3 font-mono text-[11px] uppercase tracking-wider text-vox-accent">{t(`org.roles.${u.role}`)}</td>
                      <td className="px-4 py-3 font-mono text-[11px] uppercase text-vox-dim">{u.auth_provider}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <OperatorForm index="03" />

        <section className="reveal reveal-4">
          <SectionLabel index="04">{t("admin.security")}</SectionLabel>
          <div className="mb-4 flex flex-wrap gap-2" data-testid="security-measures">
            {["rate", "brute", "csrf", "headers", "upload", "audit", "lobby"].map((k) => (
              <span key={k} className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 px-3 py-1 font-mono text-[11px] uppercase tracking-wider text-emerald-300"><ShieldCheck size={11} /> {t(`admin.sec_${k}`)}</span>
            ))}
          </div>
          {!events ? null : (
            <div className="overflow-x-auto rounded-2xl border border-vox-line">
              <table className="w-full text-sm" data-testid="audit-log-table">
                <thead className="bg-vox-deep/60 font-mono text-[11px] uppercase tracking-wider text-vox-dim">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium">{t("admin.time")}</th>
                    <th className="px-4 py-3 text-left font-medium">{t("admin.event")}</th>
                    <th className="px-4 py-3 text-left font-medium">{t("admin.who")}</th>
                    <th className="px-4 py-3 text-left font-medium">IP</th>
                    <th className="px-4 py-3 text-left font-medium">{t("admin.details")}</th>
                  </tr>
                </thead>
                <tbody>
                  {events.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-vox-dim">—</td></tr>}
                  {events.map((ev) => (
                    <tr key={ev.id} className="border-t border-vox-line" data-testid="audit-log-row">
                      <td className="px-4 py-2.5 font-mono text-xs text-vox-dim whitespace-nowrap">{new Date(ev.ts).toLocaleString(LOCALE[lang])}</td>
                      <td className="px-4 py-2.5"><span className={`rounded-full border px-2 py-0.5 font-mono text-[11px] uppercase tracking-wider ${/failed|locked|blocked|rejected|rate|deleted|removed/.test(ev.event) ? "border-vox-amber/40 text-vox-amber" : "border-vox-line text-vox-muted"}`}>{ev.event}</span></td>
                      <td className="px-4 py-2.5 text-vox-muted">{ev.email || "—"}</td>
                      <td className="px-4 py-2.5 font-mono text-xs text-vox-dim">{ev.ip || "—"}</td>
                      <td className="px-4 py-2.5 font-mono text-xs text-vox-dim">{ev.meta ? Object.entries(ev.meta).map(([k, v]) => `${k}=${v}`).join(" · ") : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
